import React, { useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowDropDown, Check } from "@mui/icons-material";

export interface PickOption {
    id: string;
    name: string;
    detail?: string;
}

interface QuickPickChipProps {
    icon: React.ReactElement;
    value: { id: string; name: string } | null;
    /** Chip text when nothing is set yet, e.g. "Set courier". */
    placeholder: string;
    menuTitle: string;
    color: "primary" | "secondary";
    loadOptions: () => Promise<PickOption[]>;
    onPick: (option: PickOption) => void;
    /** When set, the chip is read-only and explains why. */
    lockedReason?: string | null;
}

/** A chip showing the current choice; click it to pick another from a menu. */
export const QuickPickChip: React.FC<QuickPickChipProps> = ({
    icon,
    value,
    placeholder,
    menuTitle,
    color,
    loadOptions,
    onPick,
    lockedReason,
}) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [options, setOptions] = useState<PickOption[] | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    if (lockedReason) {
        return value ? (
            <Tooltip title={lockedReason}>
                <Chip icon={icon} label={value.name} size="small" color={color} variant="outlined" />
            </Tooltip>
        ) : null;
    }

    const load = () => {
        setLoadFailed(false);
        loadOptions()
            .then(setOptions)
            .catch(() => setLoadFailed(true));
    };

    const open = (el: HTMLElement) => {
        setAnchor(el);
        if (!options) load();
    };

    const pick = (option: PickOption) => {
        setAnchor(null);
        if (option.id !== value?.id) onPick(option);
    };

    return (
        <>
            <Tooltip title={`Change ${menuTitle.toLowerCase()}`}>
                <Chip
                    icon={icon}
                    label={value?.name ?? placeholder}
                    size="small"
                    color={color}
                    variant="outlined"
                    sx={value ? undefined : { borderStyle: "dashed" }}
                    onClick={(e) => open(e.currentTarget)}
                    onDelete={(e: any) => open(e.currentTarget.closest(".MuiChip-root") ?? e.currentTarget)}
                    deleteIcon={<ArrowDropDown />}
                    aria-haspopup="menu"
                    aria-expanded={!!anchor}
                />
            </Tooltip>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: "block" }}>
                    {menuTitle}
                </Typography>
                {!options && !loadFailed && (
                    <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "center" }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {loadFailed && <MenuItem onClick={load}>Couldn't load the list — tap to retry</MenuItem>}
                {options?.length === 0 && <MenuItem disabled>Nothing to choose from</MenuItem>}
                {options?.map((option) => {
                    const current = option.id === value?.id;
                    return (
                        <MenuItem key={option.id} selected={current} onClick={() => pick(option)} sx={{ minWidth: 220 }}>
                            <ListItemIcon>{current && <Check fontSize="small" />}</ListItemIcon>
                            <ListItemText primary={option.name} secondary={option.detail} />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};
